<template>
    <v-dialog v-model="dialogVisible" persistent max-width="750px">
        <v-card :loading="isLoading" :disabled="isLoading">
            <v-card-title>Bestellung ausführen</v-card-title>
            <v-card-text>
                <v-alert v-if="remainingVolume <= 0" type="warning" dense>
                    Die gesamte verfügbare Menge wurde bereits bestellt. Es können keine weiteren Bestellungen
                    aufgegeben werden.
                </v-alert>
                <v-alert v-if="!isMitarbeiter && !canOrderToday.allowed" type="warning" dense>
                    <span v-html="canOrderToday.reason"></span>
                </v-alert>
                <v-form ref="form">
                    <v-text-field v-model="orderdata.orderdaten.orderedbyName" label="Besteller"
                        readonly></v-text-field>
                    <v-text-field v-model="getPrice" label="Preis pro MWh" readonly></v-text-field>
                    <!-- Equation fields -->
                    <div v-if="isMitarbeiter"
                        style="margin: 16px 0; padding: 12px; background-color: #f8f9fa; border-radius: 6px; border: 1px solid #e9ecef;">
                        <div
                            style="display: flex; align-items: center; gap: 8px; font-size: 16px; font-weight: 500; color: #495057;">
                            <span style="font-weight: 600; color: #6c757d;">Berechnungshilfe:</span>
                            <span>{{ baseFactor.toString().replace(".", ",") }} ×</span>
                            <v-text-field v-model="baseValue" @blur="updateBasePrice" placeholder="Base" dense hide-details outlined
                                style="width: 70px; min-width: 70px;" suffix="€/MWh"></v-text-field>
                            <span v-if="this.orderdata.orderdaten.faktorPeak">+ {{
                                peakFactor.toString().replace(".", ",") }} ×</span>
                            <v-text-field v-if="this.orderdata.orderdaten.faktorPeak" v-model="peakValue"
                                @blur="updatePeakPrice" placeholder="Peak" dense hide-details outlined style="width: 70px; min-width: 70px;"
                                suffix="€/MWh"></v-text-field>
                            <span>+</span>
                            <v-text-field v-model="structValue" @blur="updateStructPrice" placeholder="Amount" dense hide-details outlined
                                style="width: 80px; min-width: 80px;" suffix="€/MWh"></v-text-field>
                            <span>=</span>
                            <span
                                style="font-weight: bold; color: #007bff; padding: 4px 8px; background-color: #e7f3ff; border-radius: 4px;">{{
                                    getPrice }}</span>
                        </div>
                    </div>

                    <v-text-field v-model="getVolume" label="Volumen in kWh" :rules="volumeRules"
                        :error="isVolumeExceeded" :error-messages="volumeErrorMessage" :hint="getVolumeHint"
                        persistent-hint></v-text-field>
                </v-form>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="primaryLight" text @click="cancleOrder" value="BESTELLUNGABBRUCH">
                    Abbrechen
                </v-btn>
                <v-btn color="primaryLight" text @click="order()" :disabled="!canOrder"> Bestellen </v-btn>
                <slot></slot>
            </v-card-actions>
        </v-card>

        <!-- Bestätigungsdialog -->
        <v-dialog v-model="confirmDialogVisible" persistent max-width="600px">
            <v-card :loading="isLoading" :disabled="isLoading">
                <v-card-title class="text-h5">
                    Bestellung bestätigen
                </v-card-title>
                <v-card-text>
                    <v-alert type="info" dense outlined class="mb-4">
                        Bitte überprüfen Sie die Bestelldaten sorgfältig vor der Bestätigung.
                    </v-alert>

                    <v-simple-table dense>
                        <template v-slot:default>
                            <tbody>
                                <tr>
                                    <td><strong>Besteller:</strong></td>
                                    <td>{{ orderdata.orderdaten.orderedbyName }}</td>
                                </tr>
                                <tr>
                                    <td><strong>Tranche:</strong></td>
                                    <td>{{ orderdata.tranchennummer }}</td>
                                </tr>
                                <tr>
                                    <td><strong>Volumen:</strong></td>
                                    <td>{{ parseInt(orderdata.orderdaten.volume).toLocaleString("de-DE") }} kWh</td>
                                </tr>
                                <tr>
                                    <td><strong>Preis:</strong></td>
                                    <td>{{ getPrice }} €/MWh</td>
                                </tr>
                                <tr>
                                    <td><strong>Geschätzte Kosten:</strong></td>
                                    <td>
                                        {{ ((orderdata.orderdaten.pricing.baseprice * orderdata.orderdaten.faktorBase +
                                            orderdata.orderdaten.pricing.peakprice * orderdata.orderdaten.faktorPeak +
                                            orderdata.orderdaten.pricing.structprice) * orderdata.orderdaten.volume / 1000)
                                            .toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                        }} €
                                    </td>
                                </tr>
                            </tbody>
                        </template>
                    </v-simple-table>

                    <v-checkbox v-model="confirmCheckbox" class="mt-4" color="primary">
                        <template v-slot:label>
                            <span style="color: #d32f2f; font-weight: 500;">
                                Hiermit bestelle ich die angegebene Tranche verbindlich!
                            </span>
                        </template>
                    </v-checkbox>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="grey" text @click="cancelConfirm">
                        Abbrechen
                    </v-btn>
                    <v-btn color="error" @click="confirmOrder" :disabled="!confirmCheckbox">
                        Kostenpflichtig bestellen
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-dialog>
</template>
<script>
import { functions } from "@/plugins/firebase"
import { httpsCallable } from "firebase/functions"
import { firestore as db } from "@/plugins/firebase"
import { doc, getDoc } from "firebase/firestore"

export default {
    data() {
        return {
            isLoading: false,
            dialogVisible: false,
            confirmDialogVisible: false,
            confirmCheckbox: false,
            userdata: {},
            holidays: {},
            companyFreeDays: {},
            calendarsLoaded: false,
            baseValueDisplay: "",
            peakValueDisplay: "",
            structValueDisplay: "",
            mitarbeiterEmails: []
        }
    },

    computed: {

        baseFactor() {
            return this.orderdata.orderdaten.faktorBase || 0
        },
        peakFactor() {
            return this.orderdata.orderdaten.faktorPeak || 0
        },
        getPrice() {
            console.log("orderdata in getPrice---", this.orderdata);
            const result = this.orderdata.orderdaten.pricing.baseprice * this.orderdata.orderdaten.faktorBase +
                this.orderdata.orderdaten.pricing.peakprice * this.orderdata.orderdaten.faktorPeak + this.orderdata.orderdaten.pricing.structprice
            return result && result.toLocaleString("de-DE")
        },
        baseValue: {
            get() {
                return this.baseValueDisplay
            },
            set(value) {
                this.baseValueDisplay = value
            }
        },
        peakValue: {
            get() {
                return this.peakValueDisplay
            },
            set(value) {
                this.peakValueDisplay = value
            }
        },
        structValue: {
            get() {
                return this.structValueDisplay
            },
            set(value) {
                this.structValueDisplay = value
            }
        },
        getVolume: {
            get() {
                const val = parseInt(this.orderdata.orderdaten.volume)
                if (isNaN(val)) return ""
                return val.toLocaleString("de-DE")
            },
            set(value) {
                // Remove thousand separators (dots) before parsing
                if (typeof value === "string") {
                    value = value.replace(/\./g, "")
                }
                this.orderdata.orderdaten.volume = parseInt(value) || 0
                console.log("Volume set to:", this.orderdata.orderdaten.volume)
            }
        },
        maxVolume() {
            if (!this.orderdata || !this.orderdata.maxTrancheMengeinkWh) return 0

            // Unterschiedliche Limits für Mitarbeiter und Kunden
            const perOrderPercent = this.isMitarbeiter ? 0.5 : 0.05  // 50% für Mitarbeiter, 5% für Kunden
            const perDayPercent = this.isMitarbeiter ? 1.0 : 0.2     // 100% für Mitarbeiter, 20% für Kunden

            // Maximal X% der Gesamtmenge pro Bestellung
            const maxPerOrder = Math.floor(this.orderdata.maxTrancheMengeinkWh * perOrderPercent)

            // Verbleibende Menge = Gesamtmenge - bereits bestellte Menge
            const remainingVolume = this.orderdata.maxTrancheMengeinkWh - (this.orderdata.alreadyOrderedVolume || 0)

            // Maximal X% der Gesamtmenge pro Tag
            const maxPerDay = Math.floor(this.orderdata.maxTrancheMengeinkWh * perDayPercent)
            const remainingTodayVolume = maxPerDay - (this.orderdata.orderedTodayVolume || 0)

            // Das kleinste der drei Limits verwenden
            return Math.min(maxPerOrder, remainingVolume, remainingTodayVolume)
        },
        isVolumeExceeded() {
            return this.editableVolume > this.maxVolume
        },
        volumeErrorMessage() {
            if (!this.isVolumeExceeded || !this.orderdata || !this.orderdata.maxTrancheMengeinkWh) return ""

            const perOrderPercent = this.isMitarbeiter ? 0.5 : 0.05
            const perDayPercent = this.isMitarbeiter ? 1.0 : 0.2
            const maxPerOrder = Math.floor(this.orderdata.maxTrancheMengeinkWh * perOrderPercent)
            const remainingVolume = this.orderdata.maxTrancheMengeinkWh - (this.orderdata.alreadyOrderedVolume || 0)
            const maxPerDay = Math.floor(this.orderdata.maxTrancheMengeinkWh * perDayPercent)
            const remainingTodayVolume = maxPerDay - (this.orderdata.orderedTodayVolume || 0)
            const alreadyOrdered = this.orderdata.alreadyOrderedVolume || 0
            const orderedToday = this.orderdata.orderedTodayVolume || 0
            const total = this.orderdata.maxTrancheMengeinkWh
            const perOrderLabel = this.isMitarbeiter ? "50%" : "5%"

            // Prüfe welches Limit verletzt wird
            if (remainingVolume <= 0) {
                return `Die gesamte Menge wurde bereits bestellt (${alreadyOrdered.toLocaleString("de-DE")} von ${total.toLocaleString("de-DE")} kWh)`
            } else if (remainingTodayVolume <= 0) {
                const msg = `Tageslimit erreicht: Heute bereits ${orderedToday.toLocaleString("de-DE")} kWh bestellt `
                return msg + `(maximal ${maxPerDay.toLocaleString("de-DE")} kWh pro Tag)`
            } else if (remainingTodayVolume < maxPerOrder) {
                return `Verbleibendes Tageslimit: ${remainingTodayVolume.toLocaleString("de-DE")} kWh (heute bereits ${orderedToday.toLocaleString("de-DE")} kWh bestellt)`
            } else if (remainingVolume < maxPerOrder) {
                const msg = `Verbleibendes Volumen: ${remainingVolume.toLocaleString("de-DE")} kWh `
                return msg + `(bereits bestellt: ${alreadyOrdered.toLocaleString("de-DE")} von ${total.toLocaleString("de-DE")} kWh)`
            } else {
                return `Volumen darf maximal ${this.maxVolume.toLocaleString("de-DE")} kWh betragen (${perOrderLabel} von ${total.toLocaleString("de-DE")} kWh)`
            }
        },
        volumeRules() {
            if (!this.orderdata || !this.orderdata.maxTrancheMengeinkWh) {
                return [() => true]
            }

            const perOrderPercent = this.isMitarbeiter ? 0.5 : 0.05
            const perDayPercent = this.isMitarbeiter ? 1.0 : 0.2
            const maxPerOrder = Math.floor(this.orderdata.maxTrancheMengeinkWh * perOrderPercent)
            const remainingVolume = this.orderdata.maxTrancheMengeinkWh - (this.orderdata.alreadyOrderedVolume || 0)
            const maxPerDay = Math.floor(this.orderdata.maxTrancheMengeinkWh * perDayPercent)
            const remainingTodayVolume = maxPerDay - (this.orderdata.orderedTodayVolume || 0)
            const alreadyOrdered = this.orderdata.alreadyOrderedVolume || 0
            const orderedToday = this.orderdata.orderedTodayVolume || 0
            const total = this.orderdata.maxTrancheMengeinkWh
            const perOrderLabel = this.isMitarbeiter ? "50%" : "5%"

            return [
                v => {
                    if (v <= this.maxVolume) return true

                    // Prüfe welches Limit verletzt wird
                    if (remainingVolume <= 0) {
                        return `Die gesamte Menge wurde bereits bestellt (${alreadyOrdered.toLocaleString("de-DE")} von ${total.toLocaleString("de-DE")} kWh)`
                    } else if (remainingTodayVolume <= 0) {
                        const msg = `Tageslimit erreicht: Heute bereits ${orderedToday.toLocaleString("de-DE")} kWh bestellt `
                        return msg + `(maximal ${maxPerDay.toLocaleString("de-DE")} kWh pro Tag)`
                    } else if (remainingTodayVolume < maxPerOrder) {
                        return `Verbleibendes Tageslimit: ${remainingTodayVolume.toLocaleString("de-DE")} kWh`
                    } else if (remainingVolume < maxPerOrder) {
                        const msg = `Verbleibendes Volumen: ${remainingVolume.toLocaleString("de-DE")} kWh `
                        return msg + `(bereits bestellt: ${alreadyOrdered.toLocaleString("de-DE")} von ${total.toLocaleString("de-DE")} kWh)`
                    } else {
                        return `Volumen darf maximal ${this.maxVolume.toLocaleString("de-DE")} kWh betragen (${perOrderLabel} von ${total.toLocaleString("de-DE")} kWh)`
                    }
                }
            ]
        },
        isMitarbeiter() {
            const email = this.$store.state.auth.profile.email;
            console.log("email----", email);
            return email && this.mitarbeiterEmails.includes(email);
        },
        getVolumeHint() {
            if (!this.orderdata || !this.orderdata.maxTrancheMengeinkWh) return ""
            const perDayPercent = this.isMitarbeiter ? 1.0 : 0.2
            const perOrderLabel = this.isMitarbeiter ? "50%" : "5%"
            const perDayLabel = this.isMitarbeiter ? "100%" : "20%"
            const alreadyOrdered = this.orderdata.alreadyOrderedVolume || 0
            const orderedToday = this.orderdata.orderedTodayVolume || 0
            const total = this.orderdata.maxTrancheMengeinkWh
            const remaining = total - alreadyOrdered
            const maxPerDay = Math.floor(total * perDayPercent)
            const remainingToday = maxPerDay - orderedToday

            const part1 = `Maximum: ${this.maxVolume.toLocaleString("de-DE")} kWh (${perOrderLabel} pro Bestellung) | `
            const part2 = `Heute: ${orderedToday.toLocaleString("de-DE")} / ${maxPerDay.toLocaleString("de-DE")} kWh (${perDayLabel}, verbleibend: ${remainingToday.toLocaleString("de-DE")} kWh) | `
            const part3 = `Gesamt: ${alreadyOrdered.toLocaleString("de-DE")} / ${total.toLocaleString("de-DE")} kWh (verbleibend: ${remaining.toLocaleString("de-DE")} kWh)`
            return part1 + part2 + part3
        },
        remainingVolume() {
            if (!this.orderdata || !this.orderdata.maxTrancheMengeinkWh) return 0
            return this.orderdata.maxTrancheMengeinkWh - (this.orderdata.alreadyOrderedVolume || 0)
        },
        canOrder() {
            const volumeCheck = this.remainingVolume > 0 && this.orderdata.orderdaten.volume > 0 && this.orderdata.orderdaten.volume <= this.maxVolume

            // Mitarbeiter können immer bestellen (wenn Volumen-Check ok)
            if (this.isMitarbeiter) {
                return volumeCheck
            }

            // Für Kunden: Zusätzliche Zeitfenster-Prüfung
            return volumeCheck && this.canOrderToday.allowed
        },
        canOrderToday() {
            if (!this.calendarsLoaded) {
                return { allowed: false, reason: "Kalenderdaten werden geladen..." }
            }

            const now = new Date()
            const dayOfWeek = now.getDay() // 0 = Sonntag, 1 = Montag, ..., 6 = Samstag
            const year = now.getFullYear()
            const dateString = `${String(now.getDate()).padStart(2, "0")}.${String(now.getMonth() + 1).padStart(2, "0")}.${year}`
            const currentHour = now.getHours()
            const currentMinute = now.getMinutes()
            const currentTime = currentHour + currentMinute / 60
console.log("Checking order permissions for today with holidays:", this.holidays, "and company free days:", this.companyFreeDays);
console.log("Current dateString:",dateString, "dayOfWeek:", dayOfWeek, "currentTime:", currentTime);
            // 1. & 2. Ist der Wochentag ein Samstag oder Sonntag?
            if (dayOfWeek === 0 || dayOfWeek === 6) {
                return { 
                    allowed: false, 
                    reason: "Bestellungen sind am Wochenende nicht möglich." 
                }
            }

            // 3. Ist das Datum ein Feiertag?
            if (this.holidays[dateString]) {
                return { 
                    allowed: false, 
                    reason: `Bestellungen sind an Feiertagen nicht möglich (${this.holidays[dateString]}).` 
                }
            }

            // 4. Ist das Datum ein firmenfreier Tag?
            if (this.companyFreeDays[dateString]) {
                // Ganztägig 09:30-16:00
                if (currentTime >= 9.5 && currentTime < 16) {
                    return { 
                        allowed: true, 
                        reason: "" 
                    }
                } else {
                    return { 
                        allowed: false, 
                        reason: `Heute können Sie zwischen 09:30 und 16:00 Uhr bestellen (${this.companyFreeDays[dateString]}).` 
                    }
                }
            }

            // 5. Ist der Wochentag Freitag?
            if (dayOfWeek === 5) {
                // 13:00-16:00
                if (currentTime >= 13 && currentTime < 16) {
                    return { allowed: true, reason: "" }
                } else {
                    return { 
                        allowed: false, 
                        reason: "Telefonische Bestellungen werden freitags von 09:30 bis 13:00 Uhr unter <a href='tel:+4921665585544'>+49 2166 558 5544</a>"+
                        " entgegengenommen.<br>Die Online-Bestellung über Energyview ist zwischen 13:00 und 16:00 Uhr möglich." 
                    }
                }
            }

            // 6. Regulärer Wochentag (Montag-Donnerstag)
            // 15:00-16:00
            if (currentTime >= 15 && currentTime < 16) {
                return { allowed: true, reason: "" }
            } else {
                return { 
                    allowed: false, 
                    reason: "Telefonische Bestellungen werden montags bis donnerstags von 09:30 bis 15:00 Uhr unter <a href='tel:+4921665585544'>+49 2166 558 5544</a>"+
                    " entgegengenommen.<br>Die Online-Bestellung über Energyview ist zwischen 15:00 und 16:00 Uhr möglich." 
                }
            }
        }
    },

    watch: {
        active() {
            this.dialogVisible = true
        },
        dialogVisible(newVal) {
            if (newVal) {
                this.initializePriceFields()
                if (!this.isMitarbeiter) {
                    this.loadCalendarData()
                }
            }
        }
    },

    methods: {
        initializePriceFields() {
            // Initialize display values with formatted pricing data
            const base = this.orderdata.orderdaten.pricing.baseprice
            const peak = this.orderdata.orderdaten.pricing.peakprice
            const struct = this.orderdata.orderdaten.pricing.structprice
            
            this.baseValueDisplay = base != null ? parseFloat(base).toFixed(4).replace(".", ",") : "0"
            this.peakValueDisplay = peak != null ? parseFloat(peak).toFixed(4).replace(".", ",") : "0"
            this.structValueDisplay = struct != null ? parseFloat(struct).toFixed(4).replace(".", ",") : ""
        },
        updateBasePrice() {
            // Parse and update baseprice when leaving the field
            let value = this.baseValueDisplay
            if (typeof value === "string") {
                value = value.replace(",", ".")
            }
            this.orderdata.orderdaten.pricing.baseprice = parseFloat(value) || 0
            console.log("****", value, parseFloat(value))
            // Reformat the display value
            this.baseValueDisplay = parseFloat(value || 0).toFixed(3).replace(".", ",")
        },
        updatePeakPrice() {
            // Parse and update peakprice when leaving the field
            let value = this.peakValueDisplay
            if (typeof value === "string") {
                value = value.replace(",", ".")
            }
            this.orderdata.orderdaten.pricing.peakprice = parseFloat(value) || 0
            // Reformat the display value
            this.peakValueDisplay = parseFloat(value || 0).toFixed(3).replace(".", ",")
        },
        updateStructPrice() {
            // Parse and update structprice when leaving the field
            let value = this.structValueDisplay
            if (typeof value === "string") {
                value = value.replace(",", ".")
            }
            this.orderdata.orderdaten.pricing.structprice = parseFloat(value) || 0
            // Reformat the display value
            this.structValueDisplay = parseFloat(value || 0).toFixed(3).replace(".", ",")
        },
        async loadCalendarData() {
            try {
                const now = new Date()
                const year = now.getFullYear().toString()

                // Lade Feiertage
                const holidaysDoc = await getDoc(doc(db, "kalender", year))
                if (holidaysDoc.exists()) {
                    this.holidays = holidaysDoc.data()
                } else {
                    this.holidays = {}
                }

                // Lade firmenfreie Tage
                const companyFreeDaysDoc = await getDoc(doc(db, "kalender_NEWfrei", year))
                if (companyFreeDaysDoc.exists()) {
                    this.companyFreeDays = companyFreeDaysDoc.data()
                } else {
                    this.companyFreeDays = {}
                }

                this.calendarsLoaded = true
            } catch (error) {
                console.error("Fehler beim Laden der Kalenderdaten:", error)
                this.calendarsLoaded = true // Trotzdem als geladen markieren
            }
        },
        order() {
            // Öffne Bestätigungsdialog statt direkt zu bestellen
            this.confirmCheckbox = false
            this.confirmDialogVisible = true
        },
        async confirmOrder() {
            const orderTranche = httpsCallable(functions, "orderTranche")
            this.isLoading = true
            
            // Berechne Preis und Kosten vor dem Absenden
            const payload = JSON.parse(JSON.stringify(this.orderdata))
            const priceEurMWh = payload.orderdaten.pricing.baseprice * payload.orderdaten.faktorBase +
                payload.orderdaten.pricing.peakprice * payload.orderdaten.faktorPeak +
                payload.orderdaten.pricing.structprice

            payload.orderdaten.pricing.price = parseFloat(priceEurMWh.toFixed(4))
            payload.orderdaten.pricing.price_cnt_kwh = parseFloat((priceEurMWh / 10).toFixed(5))
            payload.orderdaten.cost = parseFloat(((priceEurMWh * payload.orderdaten.volume) / 1000).toFixed(2))
            
            // Konvertiere Preise von €/MWh zu ct/kWh (÷ 10)
            payload.orderdaten.pricing.baseprice = parseFloat((payload.orderdaten.pricing.baseprice / 10).toFixed(4))
            payload.orderdaten.pricing.peakprice = parseFloat((payload.orderdaten.pricing.peakprice / 10).toFixed(4))
            payload.orderdaten.pricing.structprice = parseFloat((payload.orderdaten.pricing.structprice / 10).toFixed(4))

            try {
                console.log("Sending order data:", payload);
                const { data } = await orderTranche(payload)
                console.log("Received response data:", data);
                switch (data.code) {
                    case -1:
                        this.$store.commit(
                            "showSnackbar",
                            {
                                message: data.msg,
                                status: "error"
                            },
                            { root: true }
                        )
                        this.confirmDialogVisible = false
                        this.dialogVisible = false
                        break
                    case 0:
                        this.$store.commit(
                            "showSnackbar",
                            {
                                message: data.msg,
                                status: "success"
                            },
                            { root: true }
                        )
                        this.$store.state.contract.reloadOrderview = true

                        this.confirmDialogVisible = false
                        this.dialogVisible = false
                        break
                    default:
                        break
                }
            } catch (err) {
                this.$store.commit(
                    "showSnackbar",
                    {
                        message: "Es ist ein Fehler aufgetreten bei dem Versuch die Bestellung abzusenden.",
                        status: "error"
                    },
                    { root: true }
                )
                console.error(err)
            }
            this.isLoading = false
            return
        },
        cancelConfirm() {
            this.confirmDialogVisible = false
            this.confirmCheckbox = false
        },
        async loadMitarbeiterEmails() {
            try {
                const docSnap = await getDoc(doc(db, "Einstellungen", "Mitarbeiterliste"))
                if (docSnap.exists()) {
                    this.mitarbeiterEmails = docSnap.data().emails || []
                }
            } catch (error) {
                console.error("Fehler beim Laden der Mitarbeiterliste:", error)
            }
        },
        cancleOrder() {
            this.docid = ""
            this.docs = []
            this.dialogVisible = false
        }
    },

    async created() {
        await this.loadMitarbeiterEmails()
    },

    props: {
        active: Number,
        orderdata: {
            type: Object,
            default: function () {
                return {
                    maxTrancheMengeinkWh: 0,
                    alreadyOrderedVolume: 0,
                    orderedTodayVolume: 0,
                    orderdaten: {
                        orderedbyName: "",
                        faktorBase: 0,
                        faktorPeak: 0,
                        pricing: {
                            baseprice: 0,
                            peakprice: 0,
                            structprice: 0
                        }
                    }
                }
            }
        }
    }
}
</script>